import { useEffect, useState } from 'react';
import type {
  ComparisonCell,
  ComparisonResponse,
  ComparisonRow,
  ReportSynthesisResponse,
  RunChangesResponse,
  SourcesResponse,
} from '@swg/contracts';
import {
  AVAILABILITY_LABELS,
  CLASSIFICATION_LABELS,
  COMPARABILITY_STATUS_LABELS,
  CONNECTOR_RUN_STATUS_LABELS,
  METRIC_CATEGORY_LABELS,
  SOURCE_FAMILY_LABELS,
} from '@swg/domain';
import { fetchComparison, fetchRunChanges, fetchSources, fetchSynthesis } from '../api.js';
import { theme } from '../theme.js';

/**
 * Deterministische rapportviewer (M19 / US07A): vergelijkingstabellen per
 * categorie plus bronnen, actualiteit en beperkingen. Geen AI-conclusies;
 * alles komt rechtstreeks uit de opgeslagen observaties. Ontbrekende data is
 * "Niet beschikbaar", nooit 0, en feiten/schattingen zijn altijd gelabeld.
 */

const numberFormat = new Intl.NumberFormat('nl-NL', { maximumFractionDigits: 2 });

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('nl-NL', { dateStyle: 'medium', timeStyle: 'short' }).format(
    new Date(iso),
  );
}

function formatCellValue(cell: ComparisonCell): string {
  if (cell.availability !== 'AVAILABLE' || !cell.value) {
    return AVAILABILITY_LABELS[cell.availability] ?? 'Niet beschikbaar';
  }
  const v = cell.value;
  switch (v.valueType) {
    case 'NUMBER':
      return v.number === null ? AVAILABILITY_LABELS.NOT_AVAILABLE : numberFormat.format(v.number);
    case 'BOOLEAN':
      return v.boolean === null ? AVAILABILITY_LABELS.NOT_AVAILABLE : v.boolean ? 'Ja' : 'Nee';
    case 'TEXT':
      return v.text ?? AVAILABILITY_LABELS.NOT_AVAILABLE;
    case 'LIST':
      return v.list.length > 0 ? v.list.join(', ') : 'Geen';
    case 'RANGE':
      return v.low === null || v.high === null
        ? AVAILABILITY_LABELS.NOT_AVAILABLE
        : `${numberFormat.format(v.low)} – ${numberFormat.format(v.high)}`;
  }
}

const CLASSIFICATION_COLOR: Record<string, { bg: string; text: string }> = {
  FACT: { bg: '#E2F2E7', text: '#1E7A44' },
  ESTIMATE: { bg: theme.color.accentBg, text: theme.color.primary },
  INFERENCE: { bg: '#F0E7F6', text: '#6B3FA0' },
};

function ClassificationChip({ classification }: { classification: string | null }) {
  if (!classification) return null;
  const color = CLASSIFICATION_COLOR[classification] ?? {
    bg: theme.color.accentBg,
    text: theme.color.primary,
  };
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '1px 7px',
        borderRadius: 9,
        fontSize: 10.5,
        fontWeight: 600,
        background: color.bg,
        color: color.text,
        whiteSpace: 'nowrap',
      }}
    >
      {CLASSIFICATION_LABELS[classification as keyof typeof CLASSIFICATION_LABELS] ??
        classification}
    </span>
  );
}

const RELIABILITY_COLOR_MEDIUM = {
  bg: theme.color.warnBg,
  text: theme.color.warnText,
  dot: '#C9861F',
};
const RELIABILITY_COLOR: Record<string, { bg: string; text: string; dot: string }> = {
  high: { bg: '#E2F2E7', text: '#1E7A44', dot: '#2E9E5B' },
  medium: RELIABILITY_COLOR_MEDIUM,
  low: { bg: '#FBE6E6', text: '#B02A2A', dot: '#D14343' },
};

function ReliabilityChip({ cell }: { cell: ComparisonCell }) {
  if (!cell.reliability) return null;
  const c = RELIABILITY_COLOR[cell.reliability.label] ?? RELIABILITY_COLOR_MEDIUM;
  return (
    <span
      title={cell.reliability.explanation}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '1px 7px',
        borderRadius: 9,
        fontSize: 10.5,
        fontWeight: 600,
        background: c.bg,
        color: c.text,
        whiteSpace: 'nowrap',
        cursor: 'help',
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: c.dot,
          display: 'inline-block',
        }}
      />
      {cell.reliability.labelNl}
    </span>
  );
}

function CorrectionChip({ cell }: { cell: ComparisonCell }) {
  if (!cell.correction) return null;
  const protectedNote = cell.correction.protectedFromRegeneration
    ? ' (beschermd tegen regeneratie)'
    : '';
  return (
    <span
      title={`Handmatig gecorrigeerd: ${cell.correction.reason}${protectedNote}`}
      style={{
        display: 'inline-block',
        padding: '1px 7px',
        borderRadius: 9,
        fontSize: 10.5,
        fontWeight: 600,
        background: '#FBEED6',
        color: '#8A5A00',
        whiteSpace: 'nowrap',
      }}
    >
      ✎ Handmatig
    </span>
  );
}

function sourceLabel(cell: ComparisonCell): string | null {
  if (!cell.source) return null;
  const family = cell.source.family
    ? (SOURCE_FAMILY_LABELS[cell.source.family] ?? cell.source.family)
    : null;
  return family;
}

function ComparisonTable({
  rows,
  subjects,
}: {
  rows: ComparisonRow[];
  subjects: ComparisonResponse['subjects'];
}) {
  const cellStyle = {
    padding: '10px 12px',
    borderBottom: `1px solid ${theme.color.border}`,
    fontSize: 13,
    verticalAlign: 'top' as const,
  };
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th
              style={{ ...cellStyle, textAlign: 'left', color: theme.color.muted, fontWeight: 600 }}
            >
              Metriek
            </th>
            {subjects.map((s) => (
              <th
                key={s.key}
                style={{ ...cellStyle, textAlign: 'left', fontWeight: s.isOwnCompany ? 700 : 600 }}
              >
                {s.name}
                {s.isOwnCompany && (
                  <span style={{ color: theme.color.muted, fontWeight: 400 }}> (eigen)</span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <RowGroup key={row.metricCode} row={row} subjects={subjects} cellStyle={cellStyle} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RowGroup({
  row,
  subjects,
  cellStyle,
}: {
  row: ComparisonRow;
  subjects: ComparisonResponse['subjects'];
  cellStyle: React.CSSProperties;
}) {
  const cellsByKey = new Map(row.cells.map((c) => [c.subjectKey, c]));
  const hasWarnings = row.comparability.warnings.length > 0;
  return (
    <>
      <tr>
        <td style={cellStyle}>
          <div style={{ fontWeight: 600 }}>{row.nameNl}</div>
          <div style={{ fontSize: 11.5, color: theme.color.muted }}>
            {row.unit ? `${row.unit} · ` : ''}
            {COMPARABILITY_STATUS_LABELS[row.comparability.status]}
          </div>
        </td>
        {subjects.map((s) => {
          const cell = cellsByKey.get(s.key);
          if (!cell)
            return (
              <td key={s.key} style={cellStyle}>
                —
              </td>
            );
          const unavailable = cell.availability !== 'AVAILABLE';
          const src = sourceLabel(cell);
          return (
            <td key={s.key} style={cellStyle}>
              <div style={{ color: unavailable ? theme.color.subtle : theme.color.text }}>
                {formatCellValue(cell)}
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  marginTop: 3,
                  flexWrap: 'wrap',
                }}
              >
                <ClassificationChip classification={unavailable ? null : cell.classification} />
                {!unavailable && <ReliabilityChip cell={cell} />}
                {!unavailable && <CorrectionChip cell={cell} />}
                {src && !unavailable && !cell.correction && (
                  <span style={{ fontSize: 10.5, color: theme.color.muted }}>{src}</span>
                )}
              </div>
            </td>
          );
        })}
      </tr>
      {hasWarnings && (
        <tr>
          <td
            colSpan={subjects.length + 1}
            style={{
              padding: '6px 12px 10px',
              borderBottom: `1px solid ${theme.color.border}`,
              background: theme.color.warnBg,
              fontSize: 12,
              color: theme.color.warnText,
            }}
          >
            {row.comparability.warnings.map((w) => (
              <div key={w.code}>⚠ {w.message}</div>
            ))}
          </td>
        </tr>
      )}
    </>
  );
}

/** Kleur per connectorstatus voor de bronnenweergave. */
function connectorStatusColor(status: string): string {
  if (status === 'SUCCESS') return '#1E7A44';
  if (status === 'PARTIAL') return '#8A5A00';
  if (status === 'FAILED') return '#B4232B';
  return theme.color.muted; // SKIPPED/CANCELLED/PENDING/RUNNING
}

const AI_ACCENT = '#7c3aed';

/** Toont de AI-conceptsynthese, duidelijk gelabeld en gescheiden van de feiten. */
function AiSynthesisCard({
  synthesis,
  card,
}: {
  synthesis: ReportSynthesisResponse;
  card: React.CSSProperties;
}) {
  const sections: { label: string; items: { text: string }[] }[] = [
    { label: 'Sterktes', items: synthesis.strengths },
    { label: 'Zwaktes', items: synthesis.weaknesses },
    { label: 'Kansen', items: synthesis.opportunities },
    { label: 'Bedreigingen', items: synthesis.threats },
    { label: 'Aanbevelingen', items: synthesis.recommendations },
  ];
  const generated = synthesis.generatedAt
    ? new Intl.DateTimeFormat('nl-NL', { dateStyle: 'medium', timeStyle: 'short' }).format(
        new Date(synthesis.generatedAt),
      )
    : null;
  const totalTokens =
    synthesis.promptTokens !== null && synthesis.completionTokens !== null
      ? synthesis.promptTokens + synthesis.completionTokens
      : null;
  const tokenText =
    totalTokens !== null
      ? `${new Intl.NumberFormat('nl-NL').format(totalTokens)} tokens (prompt ${new Intl.NumberFormat('nl-NL').format(synthesis.promptTokens ?? 0)} · antwoord ${new Intl.NumberFormat('nl-NL').format(synthesis.completionTokens ?? 0)})`
      : null;
  const costText =
    synthesis.costEur !== null
      ? new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(
          synthesis.costEur,
        )
      : null;

  return (
    <div style={{ ...card, borderLeft: `4px solid ${AI_ACCENT}` }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 4, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>AI-samenvatting</h2>
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: '#fff',
            background: AI_ACCENT,
            borderRadius: 6,
            padding: '2px 7px',
          }}
        >
          AI
        </span>
        <span style={{ fontSize: 11.5, color: theme.color.muted }}>
          concept · geen feiten{synthesis.model ? ` · ${synthesis.model}` : ''}
          {generated ? ` · ${generated}` : ''}
        </span>
      </div>
      <p style={{ margin: '0 0 6px', fontSize: 12, color: theme.color.muted }}>
        Door AI samengevat uit de onderstaande observaties; controleer altijd zelf. Ontbrekende
        gegevens blijven onbekend en worden nooit ingevuld.
      </p>
      {(tokenText || costText) && (
        <p style={{ margin: '0 0 12px', fontSize: 11.5, color: theme.color.muted }}>
          Verbruik: {tokenText ?? 'onbekend'}
          {costText ? ` · geschatte kosten ${costText}` : ''}
        </p>
      )}

      {synthesis.managementSummary && (
        <p style={{ margin: '0 0 14px', fontSize: 13.5, lineHeight: 1.5 }}>
          {synthesis.managementSummary}
        </p>
      )}

      {sections
        .filter((s) => s.items.length > 0)
        .map((s) => (
          <div key={s.label} style={{ marginBottom: 10 }}>
            <h3 style={{ margin: '0 0 4px', fontSize: 13, fontWeight: 700 }}>{s.label}</h3>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.45 }}>
              {s.items.map((item, i) => (
                <li key={i}>{item.text}</li>
              ))}
            </ul>
          </div>
        ))}

      {synthesis.droppedClaims !== null && synthesis.droppedClaims > 0 && (
        <p style={{ margin: '8px 0 0', fontSize: 11.5, color: theme.color.muted }}>
          {synthesis.droppedClaims} ongefundeerde uitspraak/uitspraken zonder geldige bron zijn
          weggelaten.
        </p>
      )}
    </div>
  );
}

type ReportTab = 'samenvatting' | 'vergelijking' | 'bronnen';

export function ReportView({ runId }: { runId: string }) {
  const [state, setState] = useState<
    | { status: 'loading' }
    | {
        status: 'ready';
        comparison: ComparisonResponse;
        sources: SourcesResponse;
        changes: RunChangesResponse | null;
        synthesis: ReportSynthesisResponse | null;
      }
    | { status: 'error' }
  >({ status: 'loading' });
  const [tab, setTab] = useState<ReportTab>('samenvatting');
  const [shared, setShared] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [comparison, sources] = await Promise.all([
          fetchComparison(runId),
          fetchSources(runId),
        ]);
        // Wijzigingsvergelijking en AI-synthese zijn optioneel (kunnen ontbreken).
        const [changes, synthesis] = await Promise.all([
          fetchRunChanges(runId).catch(() => null),
          fetchSynthesis(runId).catch(() => null),
        ]);
        if (!cancelled) setState({ status: 'ready', comparison, sources, changes, synthesis });
      } catch {
        if (!cancelled) setState({ status: 'error' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [runId]);

  const card = {
    background: theme.color.surface,
    border: `1px solid ${theme.color.border}`,
    borderRadius: 14,
    padding: '22px 26px',
    marginBottom: 16,
  } as const;

  if (state.status === 'loading') {
    return (
      <div style={card}>
        <p style={{ margin: 0, color: theme.color.muted, fontSize: 13.5 }}>Rapport laden…</p>
      </div>
    );
  }
  if (state.status === 'error') {
    return (
      <div style={card}>
        <p style={{ margin: 0, color: theme.color.muted, fontSize: 13.5 }}>
          Het rapport kon niet worden geladen.
        </p>
      </div>
    );
  }

  const { comparison, sources, changes, synthesis } = state;
  const categories = [...new Set(comparison.rows.map((r) => r.category))];
  const srcCount = (st: string) => sources.sources.filter((s) => s.status === st).length;
  const srcStats = {
    total: sources.sources.length,
    ok: srcCount('SUCCESS'),
    partial: srcCount('PARTIAL'),
    failed: srcCount('FAILED'),
    skipped: srcCount('SKIPPED') + srcCount('CANCELLED'),
  };
  const failedSources = sources.sources.filter((s) =>
    ['FAILED', 'SKIPPED', 'CANCELLED'].includes(s.status),
  );
  const limitations: string[] = [];
  if (sources.failureSummary) limitations.push(sources.failureSummary);
  if (failedSources.length > 0) {
    limitations.push(
      `${failedSources.length} bronaanroep${failedSources.length === 1 ? '' : 'en'} zonder resultaat (zie bronnenoverzicht); de bijbehorende waarden zijn "Niet beschikbaar".`,
    );
  }
  if (comparison.rows.length === 0) {
    limitations.push(
      'Er zijn nog geen observaties verzameld. Controleer of er connectors zijn ingeschakeld (feature flag) en geconfigureerd.',
    );
  }

  const exportBtn = {
    background: theme.color.surface,
    border: `1px solid ${theme.color.borderStrong}`,
    borderRadius: 8,
    padding: '8px 14px',
    fontSize: 13,
    fontWeight: 500,
    color: theme.color.text,
    textDecoration: 'none',
    cursor: 'pointer',
    fontFamily: 'inherit',
    display: 'inline-block',
  } as const;

  async function onShare() {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setShared(true);
      setTimeout(() => setShared(false), 2500);
    } catch {
      setShared(false);
    }
  }

  const TABS: { key: ReportTab; label: string }[] = [
    { key: 'samenvatting', label: 'Samenvatting' },
    { key: 'vergelijking', label: 'Vergelijking' },
    { key: 'bronnen', label: 'Bronnen & betrouwbaarheid' },
  ];

  return (
    <>
      {/* Toolbar: export en delen (conform prototype). "Opnieuw uitvoeren" staat
          in de kop van het analysescherm. */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
        <a href={`/api/analysis-runs/${encodeURIComponent(runId)}/export.pdf`} style={exportBtn}>
          Exporteer PDF
        </a>
        <a href={`/api/analysis-runs/${encodeURIComponent(runId)}/export.csv`} style={exportBtn}>
          Exporteer Excel (CSV)
        </a>
        <button type="button" onClick={() => void onShare()} style={exportBtn}>
          {shared ? 'Link gekopieerd ✓' : 'Delen'}
        </button>
      </div>

      {/* Legenda */}
      <div
        style={{
          ...card,
          marginBottom: 18,
          padding: '11px 16px',
          display: 'flex',
          gap: 18,
          alignItems: 'center',
          flexWrap: 'wrap',
          fontSize: 12,
        }}
      >
        <span style={{ fontWeight: 600, color: theme.color.muted, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: 11 }}>
          Legenda
        </span>
        <span>
          <span style={{ background: theme.color.accentBg, color: theme.color.primary, borderRadius: 9, padding: '2px 8px', fontWeight: 600, fontSize: 10.5 }}>FEIT</span> vastgesteld uit bron
        </span>
        <span>
          <span style={{ background: theme.color.warnBg, color: '#8A6A12', borderRadius: 9, padding: '2px 8px', fontWeight: 600, fontSize: 10.5 }}>SCHATTING</span> afgeleid, met bandbreedte
        </span>
        <span>
          <span style={{ background: '#F0E8F5', color: '#6B3E8F', borderRadius: 9, padding: '2px 8px', fontWeight: 600, fontSize: 10.5 }}>AI</span> gegenereerde interpretatie of aanbeveling
        </span>
        <span style={{ borderLeft: `1px solid ${theme.color.border}`, paddingLeft: 18 }}>
          Betrouwbaarheid: <span style={{ color: '#157A4A', fontWeight: 600 }}>● hoog</span>{' '}
          <span style={{ color: '#8A6A12', fontWeight: 600 }}>● middel</span>{' '}
          <span style={{ color: '#A33B2E', fontWeight: 600 }}>● laag</span>
        </span>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, borderBottom: `2px solid ${theme.color.border}`, marginBottom: 22 }}>
        {TABS.map((t) => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              style={{
                background: 'none',
                border: 'none',
                borderBottom: `2.5px solid ${active ? theme.color.primary : 'transparent'}`,
                marginBottom: -2,
                padding: '10px 16px',
                fontSize: 14,
                fontWeight: active ? 700 : 500,
                color: active ? theme.color.text : theme.color.muted,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {/* ── Tab: Samenvatting ── */}
      {tab === 'samenvatting' &&
        (synthesis?.available ? (
          <AiSynthesisCard synthesis={synthesis} card={card} />
        ) : (
          <div style={card}>
            <h2 style={{ margin: '0 0 6px', fontSize: 15, fontWeight: 700 }}>Managementsamenvatting</h2>
            <p style={{ margin: 0, fontSize: 13.5, color: theme.color.muted }}>
              Er is (nog) geen AI-samenvatting voor deze uitvoering. De feitelijke vergelijking staat
              onder het tabblad <strong>Vergelijking</strong>.
            </p>
          </div>
        ))}

      {/* ── Tab: Vergelijking ── */}
      {tab === 'vergelijking' && (
        <>
          <div style={card}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 4 }}>
              <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Vergelijking</h2>
              <span style={{ fontSize: 11.5, color: theme.color.muted }}>
                deterministisch · zonder AI-conclusies · regelversie {comparison.comparisonRuleVersion}
              </span>
            </div>
            {comparison.rows.length === 0 ? (
              <p style={{ margin: '10px 0 0', fontSize: 13.5, color: theme.color.muted }}>
                Nog geen vergelijkingsdata beschikbaar.
              </p>
            ) : (
              categories.map((category) => (
                <div key={category} style={{ marginTop: 18 }}>
                  <h3
                    style={{
                      margin: '0 0 6px',
                      fontSize: 13.5,
                      fontWeight: 700,
                      color: theme.color.primaryDark,
                    }}
                  >
                    {METRIC_CATEGORY_LABELS[category as keyof typeof METRIC_CATEGORY_LABELS] ??
                      category}
                  </h3>
                  <ComparisonTable
                    rows={comparison.rows.filter((r) => r.category === category)}
                    subjects={comparison.subjects}
                  />
                </div>
              ))
            )}
          </div>

          {/* Bronconflicten (M18/US06) */}
          {comparison.conflicts.length > 0 && (
            <div style={card}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 4 }}>
                <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Bronconflicten</h2>
                <span style={{ fontSize: 11.5, color: theme.color.muted }}>
                  tegenstrijdige waarden tussen bronnen · handmatige controle aanbevolen
                </span>
              </div>
              <ul style={{ margin: '10px 0 0', paddingLeft: 18, fontSize: 13, color: theme.color.text }}>
                {comparison.conflicts.map((c) => {
                  const subject = comparison.subjects.find(
                    (s) => s.subjectId === c.subjectId && s.subjectType === c.subjectType,
                  );
                  const metric = comparison.rows.find((r) => r.metricCode === c.metricCode);
                  const sev = c.severity === 'HIGH' ? 'Hoog' : c.severity === 'LOW' ? 'Laag' : 'Middel';
                  return (
                    <li key={`${c.metricCode}:${c.subjectType}:${c.subjectId}`} style={{ marginBottom: 4 }}>
                      <strong>{metric?.nameNl ?? c.metricCode}</strong>
                      {subject ? ` — ${subject.name}` : ''}: {c.description}{' '}
                      <span style={{ color: theme.color.muted, fontSize: 11.5 }}>(ernst: {sev})</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </>
      )}

      {/* ── Tab: Bronnen & betrouwbaarheid ── */}
      {tab === 'bronnen' && (
      <>
      <div style={card}>
        <h2 style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 700 }}>Bronnen en actualiteit</h2>
        {srcStats.total > 0 && (
          <p style={{ margin: '2px 0 0', fontSize: 12.5, color: theme.color.muted }}>
            <strong style={{ color: connectorStatusColor('SUCCESS') }}>{srcStats.ok}</strong> van{' '}
            {srcStats.total} bronaanroepen gelukt
            {srcStats.partial > 0 && ` · ${srcStats.partial} gedeeltelijk`}
            {srcStats.failed > 0 && (
              <>
                {' · '}
                <strong style={{ color: connectorStatusColor('FAILED') }}>
                  {srcStats.failed} mislukt
                </strong>
              </>
            )}
            {srcStats.skipped > 0 && ` · ${srcStats.skipped} overgeslagen`}
          </p>
        )}
        {sources.sources.length === 0 ? (
          <p style={{ margin: '10px 0 0', fontSize: 13.5, color: theme.color.muted }}>
            Er zijn geen bronaanroepen geregistreerd voor deze uitvoering.
          </p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
              <thead>
                <tr>
                  {['Bron', 'Subject', 'Status', 'Opgehaald', 'Versie'].map((h) => (
                    <th
                      key={h}
                      style={{
                        textAlign: 'left',
                        padding: '8px 12px',
                        borderBottom: `1px solid ${theme.color.border}`,
                        fontSize: 12,
                        color: theme.color.muted,
                        fontWeight: 600,
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sources.sources.map((s) => (
                  <tr key={s.id}>
                    <td
                      style={{
                        padding: '8px 12px',
                        borderBottom: `1px solid ${theme.color.border}`,
                        fontSize: 13,
                      }}
                    >
                      {s.connectorName}
                    </td>
                    <td
                      style={{
                        padding: '8px 12px',
                        borderBottom: `1px solid ${theme.color.border}`,
                        fontSize: 13,
                      }}
                    >
                      {s.subjectName}
                    </td>
                    <td
                      style={{
                        padding: '8px 12px',
                        borderBottom: `1px solid ${theme.color.border}`,
                        fontSize: 13,
                      }}
                    >
                      <span style={{ color: connectorStatusColor(s.status), fontWeight: 600 }}>
                        {CONNECTOR_RUN_STATUS_LABELS[s.status] ?? s.status}
                      </span>
                      {s.servedFromCache && (
                        <span
                          title="Hergebruikt uit een recente check van hetzelfde domein in een ander project (geen nieuwe API-aanroep). De ophaaldatum toont wanneer de data oorspronkelijk is opgehaald."
                          style={{
                            marginLeft: 6,
                            fontSize: 11,
                            fontWeight: 600,
                            color: theme.color.muted,
                            border: `1px solid ${theme.color.border}`,
                            borderRadius: 6,
                            padding: '1px 6px',
                          }}
                        >
                          ♻ uit cache
                        </span>
                      )}
                      {s.errorCode && (
                        <span
                          title={s.errorMessage ?? undefined}
                          style={{ color: theme.color.warnText, fontSize: 11.5 }}
                        >
                          {' '}
                          ({s.errorCode})
                        </span>
                      )}
                      {s.errorMessage && (
                        <div style={{ fontSize: 11, color: theme.color.muted, marginTop: 2 }}>
                          {s.errorMessage}
                        </div>
                      )}
                    </td>
                    <td
                      style={{
                        padding: '8px 12px',
                        borderBottom: `1px solid ${theme.color.border}`,
                        fontSize: 13,
                      }}
                    >
                      {formatDate(s.retrievedAt ?? s.finishedAt)}
                    </td>
                    <td
                      style={{
                        padding: '8px 12px',
                        borderBottom: `1px solid ${theme.color.border}`,
                        fontSize: 13,
                      }}
                    >
                      {s.connectorVersion ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Wijzigingen t.o.v. de vorige uitvoering (M24) */}
      {changes && changes.hasBaseline && (
        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 8 }}>
            <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>
              Wijzigingen t.o.v. vorige uitvoering
            </h2>
            <span style={{ fontSize: 11.5, color: theme.color.muted }}>
              run {changes.baselineRunNumber} → {changes.currentRunNumber} · regelversie{' '}
              {changes.comparisonRuleVersion}
            </span>
          </div>
          {changes.observationChanges.length === 0 && changes.competitorChanges.every(
            (c) => c.changeType === 'UNCHANGED',
          ) ? (
            <p style={{ margin: 0, fontSize: 13.5, color: theme.color.muted }}>
              Geen noemenswaardige wijzigingen ten opzichte van de vorige uitvoering.
            </p>
          ) : (
            <>
              {changes.competitorChanges.some((c) => c.changeType !== 'UNCHANGED') && (
                <p style={{ margin: '0 0 8px', fontSize: 13 }}>
                  Concurrenten:{' '}
                  {changes.competitorChanges
                    .filter((c) => c.changeType !== 'UNCHANGED')
                    .map((c) => `${c.name} (${c.changeType === 'ADDED' ? 'toegevoegd' : 'verwijderd'})`)
                    .join(', ')}
                </p>
              )}
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
                {changes.observationChanges.map((c) => (
                  <li key={`${c.subjectId}-${c.metricCode}`} style={{ marginBottom: 3 }}>
                    <strong>{c.metricNameNl}</strong>
                    {c.oldText !== null && c.newText !== null ? `: ${c.oldText} → ${c.newText}` : ''}{' '}
                    <span style={{ color: theme.color.muted }}>— {c.explanation}</span>
                    {c.significance === 'MAJOR' && (
                      <span style={{ color: theme.color.warnText, fontWeight: 600 }}> (opvallend)</span>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      {/* Beperkingen */}
      {limitations.length > 0 && (
        <div
          style={{
            ...card,
            background: theme.color.warnBg,
            border: `1px solid ${theme.color.warnBorder}`,
          }}
        >
          <h2
            style={{
              margin: '0 0 8px',
              fontSize: 15,
              fontWeight: 700,
              color: theme.color.warnText,
            }}
          >
            Beperkingen
          </h2>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: theme.color.warnText }}>
            {limitations.map((l) => (
              <li key={l}>{l}</li>
            ))}
          </ul>
        </div>
      )}
      </>
      )}
    </>
  );
}
